import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { 
  Mail, 
  MessageSquare, 
  Phone, 
  Send, 
  Users, 
  TrendingUp, 
  Clock, 
  CheckCircle,
  Plus,
  Filter,
  Search
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const EnhancedCommunications = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('email');
  const [showComposeModal, setShowComposeModal] = useState(false);
  const [composeType, setComposeType] = useState('email');
  const [formData, setFormData] = useState({
    recipients: '',
    subject: '',
    title: '',
    message: ''
  });

  const handleSendNow = () => {
    // Validate form
    if (!formData.recipients || !formData.message) {
      alert('Please fill in recipients and message');
      return;
    }

    if (composeType === 'email' && !formData.subject) {
      alert('Please enter email subject');
      return;
    }

    // In production, call API to send
    console.log('Sending:', composeType, formData);
    alert(`${composeType.toUpperCase()} sent successfully!`);
    
    // Reset form and close modal
    setFormData({ recipients: '', subject: '', title: '', message: '' });
    setShowComposeModal(false);
  };

  // Email campaigns
  const emailCampaigns = [
    {
      id: 1,
      subject: 'Welcome to ReferBridge - Getting Started Guide',
      recipients: 156,
      sent: 156,
      opened: 142,
      clicked: 89,
      status: 'sent',
      date: '2025-11-07',
      type: 'automated'
    },
    {
      id: 2,
      subject: 'New Referral Received - Action Required',
      recipients: 45,
      sent: 45,
      opened: 43,
      clicked: 38,
      status: 'sent',
      date: '2025-11-08',
      type: 'transactional'
    },
    {
      id: 3,
      subject: 'Monthly Newsletter - November 2025',
      recipients: 342,
      sent: 0,
      opened: 0,
      clicked: 0,
      status: 'scheduled',
      date: '2025-11-15',
      type: 'marketing'
    }
  ];

  // SMS messages
  const smsMessages = [
    {
      id: 1,
      message: 'Your referral appointment is confirmed for tomorrow at 2 PM',
      recipients: 23,
      delivered: 23,
      status: 'delivered',
      date: '2025-11-08',
      type: 'appointment'
    },
    {
      id: 2,
      message: 'Reminder: Complete your patient intake form before your visit',
      recipients: 45,
      delivered: 43,
      status: 'delivered',
      date: '2025-11-07',
      type: 'reminder'
    },
    {
      id: 3,
      message: 'Dr. Gray reviewed your referral. Click link for details',
      recipients: 12,
      delivered: 12,
      status: 'delivered',
      date: '2025-11-06',
      type: 'notification'
    }
  ];

  // In-app notifications
  const notifications = [
    {
      id: 1,
      title: 'New Referral Received',
      message: 'Dr. Sarah Johnson sent a new referral for wisdom teeth extraction',
      recipients: 3,
      read: 3,
      status: 'delivered',
      date: '2025-11-08',
      priority: 'high'
    },
    {
      id: 2,
      title: 'Appointment Reminder',
      message: 'Patient John Doe has an appointment tomorrow at 10 AM',
      recipients: 1,
      read: 1,
      status: 'delivered',
      date: '2025-11-07',
      priority: 'medium'
    },
    {
      id: 3,
      title: 'System Update',
      message: 'New features available: enhanced analytics and reporting',
      recipients: 342,
      read: 287,
      status: 'delivered',
      date: '2025-11-06',
      priority: 'low'
    }
  ];

  const getStatusBadge = (status) => {
    const colors = {
      sent: 'bg-green-100 text-green-800 border-green-200',
      scheduled: 'bg-blue-100 text-blue-800 border-blue-200',
      delivered: 'bg-green-100 text-green-800 border-green-200',
      failed: 'bg-red-100 text-red-800 border-red-200'
    };
    return <Badge className={colors[status] || 'bg-gray-100 text-gray-800'}>{status}</Badge>;
  };

  return (
    <div>
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-[#0A1628] mb-2 flex items-center">
            <MessageSquare className="h-8 w-8 mr-3 text-[#D4AF37]" />
            Communications Center
          </h1>
          <p className="text-gray-600">Manage emails, SMS, and in-app notifications</p>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid md:grid-cols-3 gap-4 mb-8">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Email Sent (30 days)</p>
                <p className="text-2xl font-bold text-[#0A1628]">1,247</p>
              </div>
              <Mail className="h-8 w-8 text-[#D4AF37]" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">SMS Delivered</p>
                <p className="text-2xl font-bold text-[#0A1628]">856</p>
              </div>
              <MessageSquare className="h-8 w-8 text-[#D4AF37]" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Avg Open Rate</p>
                <p className="text-2xl font-bold text-[#0A1628]">68%</p>
              </div>
              <TrendingUp className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <div className="flex space-x-2 mb-6">
        <Button
          variant={activeTab === 'email' ? 'default' : 'outline'}
          onClick={() => setActiveTab('email')}
          className={activeTab === 'email' ? 'bg-[#0A1628] text-[#D4AF37]' : ''}
        >
          <Mail className="h-4 w-4 mr-2" />
          Email
        </Button>
        <Button
          variant={activeTab === 'sms' ? 'default' : 'outline'}
          onClick={() => setActiveTab('sms')}
          className={activeTab === 'sms' ? 'bg-[#0A1628] text-[#D4AF37]' : ''}
        >
          <MessageSquare className="h-4 w-4 mr-2" />
          SMS
        </Button>
        <Button
          variant={activeTab === 'notifications' ? 'default' : 'outline'}
          onClick={() => setActiveTab('notifications')}
          className={activeTab === 'notifications' ? 'bg-[#0A1628] text-[#D4AF37]' : ''}
        >
          <Users className="h-4 w-4 mr-2" />
          In-App Notifications
        </Button>
      </div>

      {/* Email Tab */}
      {activeTab === 'email' && (
        <>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-[#0A1628]">Email Campaigns</h2>
            <Button 
              className="bg-[#D4AF37] text-[#0A1628] hover:bg-[#C19B2B]"
              onClick={() => { setComposeType('email'); setShowComposeModal(true); }}
            >
              <Plus className="h-4 w-4 mr-2" />
              New Email
            </Button>
          </div>

          <div className="space-y-4">
            {emailCampaigns.map((campaign) => (
              <Card key={campaign.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <h3 className="font-semibold text-lg text-[#0A1628]">{campaign.subject}</h3>
                        {getStatusBadge(campaign.status)}
                        <Badge variant="outline">{campaign.type}</Badge>
                      </div>
                      <p className="text-sm text-gray-600">Sent: {campaign.date}</p>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-4 gap-4 pt-4 border-t">
                    <div className="text-center">
                      <p className="text-sm text-gray-600">Recipients</p>
                      <p className="text-xl font-bold text-[#0A1628]">{campaign.recipients}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-gray-600">Opened</p>
                      <p className="text-xl font-bold text-[#D4AF37]">{campaign.opened}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-gray-600">Clicked</p>
                      <p className="text-xl font-bold text-blue-600">{campaign.clicked}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-gray-600">Open Rate</p>
                      <p className="text-xl font-bold text-green-600">
                        {campaign.sent > 0 ? Math.round((campaign.opened / campaign.sent) * 100) : 0}%
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}

      {/* SMS Tab */}
      {activeTab === 'sms' && (
        <>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-[#0A1628]">SMS Messages</h2>
            <Button 
              className="bg-[#D4AF37] text-[#0A1628] hover:bg-[#C19B2B]"
              onClick={() => { setComposeType('sms'); setShowComposeModal(true); }}
            >
              <Plus className="h-4 w-4 mr-2" />
              New SMS
            </Button>
          </div>

          <div className="space-y-4">
            {smsMessages.map((sms) => (
              <Card key={sms.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        {getStatusBadge(sms.status)}
                        <Badge variant="outline">{sms.type}</Badge>
                      </div>
                      <p className="text-gray-700 mb-2">{sms.message}</p>
                      <p className="text-sm text-gray-600">Sent: {sms.date}</p>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-3 gap-4 pt-4 border-t">
                    <div className="text-center">
                      <p className="text-sm text-gray-600">Recipients</p>
                      <p className="text-xl font-bold text-[#0A1628]">{sms.recipients}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-gray-600">Delivered</p>
                      <p className="text-xl font-bold text-[#D4AF37]">{sms.delivered}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-gray-600">Delivery Rate</p>
                      <p className="text-xl font-bold text-green-600">
                        {sms.recipients > 0 ? Math.round((sms.delivered / sms.recipients) * 100) : 0}%
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}

      {/* Notifications Tab */}
      {activeTab === 'notifications' && (
        <>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-[#0A1628]">In-App Notifications</h2>
            <Button 
              className="bg-[#D4AF37] text-[#0A1628] hover:bg-[#C19B2B]"
              onClick={() => { setComposeType('notification'); setShowComposeModal(true); }}
            >
              <Plus className="h-4 w-4 mr-2" />
              New Notification
            </Button>
          </div>

          <div className="space-y-4">
            {notifications.map((notification) => (
              <Card key={notification.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <h3 className="font-semibold text-lg text-[#0A1628]">{notification.title}</h3>
                        {getStatusBadge(notification.status)}
                        <Badge 
                          variant="outline"
                          className={
                            notification.priority === 'high' ? 'border-red-500 text-red-500' :
                            notification.priority === 'medium' ? 'border-yellow-500 text-yellow-500' :
                            'border-gray-500 text-gray-500'
                          }
                        >
                          {notification.priority}
                        </Badge>
                      </div>
                      <p className="text-gray-700 mb-2">{notification.message}</p>
                      <p className="text-sm text-gray-600">Sent: {notification.date}</p>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-3 gap-4 pt-4 border-t">
                    <div className="text-center">
                      <p className="text-sm text-gray-600">Recipients</p>
                      <p className="text-xl font-bold text-[#0A1628]">{notification.recipients}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-gray-600">Read</p>
                      <p className="text-xl font-bold text-[#D4AF37]">{notification.read}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-gray-600">Read Rate</p>
                      <p className="text-xl font-bold text-green-600">
                        {notification.recipients > 0 ? Math.round((notification.read / notification.recipients) * 100) : 0}%
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}

      {/* Compose Modal */}
      {showComposeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold text-[#0A1628] mb-4">
              Compose {composeType === 'email' ? 'Email' : composeType === 'sms' ? 'SMS' : 'Notification'}
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Recipients</label>
                <Input 
                  placeholder="Select recipients..." 
                  value={formData.recipients}
                  onChange={(e) => setFormData({...formData, recipients: e.target.value})}
                />
              </div>
              {composeType === 'email' && (
                <div>
                  <label className="block text-sm font-medium mb-2">Subject</label>
                  <Input 
                    placeholder="Email subject" 
                    value={formData.subject}
                    onChange={(e) => setFormData({...formData, subject: e.target.value})}
                  />
                </div>
              )}
              {composeType === 'notification' && (
                <div>
                  <label className="block text-sm font-medium mb-2">Title</label>
                  <Input 
                    placeholder="Notification title" 
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium mb-2">Message</label>
                <Textarea 
                  placeholder={`Enter ${composeType} message...`}
                  rows={composeType === 'sms' ? 4 : 8}
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                />
              </div>
              <div className="flex space-x-2">
                <Button 
                  className="flex-1 bg-[#D4AF37] text-[#0A1628] hover:bg-[#C19B2B]"
                  onClick={handleSendNow}
                >
                  <Send className="h-4 w-4 mr-2" />
                  Send Now
                </Button>
                <Button onClick={() => setShowComposeModal(false)} variant="outline" className="flex-1">
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnhancedCommunications;
